/**
 * Response Coverage cell (spec §4.1/§4.2) — shared by the Compliance Matrix
 * and Evaluation Criteria tables.
 *
 * Per narrative requirement: section chips (deep-link to the editor, ✕ to
 * unlink on hover), derived status pill, hover-revealed "+ Map" picker, and
 * dashed pending-suggestion chips (✓ accept / ✕ reject — nothing links
 * without an explicit accept).
 */

import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus, X, Check, Flag } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { useCoverageStore } from '@/stores/coverage-store'
import { useSectionFlagsStore, mergeFlagsByArtifactSection } from '@/stores/section-flags-store'
import { coverageStatusConfig, abbreviateArtifact } from '@/components/requirement-coverage/coverage-status'
import { SectionPickerPopover } from '@/components/requirement-coverage/SectionPickerPopover'
import { cn } from '@/lib/utils'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'

interface CoverageCellProps {
  bidId: string
  requirementId: string
  requirementKind: 'compliance' | 'criterion'
  readOnly?: boolean
}

export function CoverageCell({ bidId, requirementId, requirementKind, readOnly }: CoverageCellProps) {
  const coverage = useCoverageStore((s) => s.coverage)
  const suggestions = useCoverageStore((s) => s.suggestions)
  const link = useCoverageStore((s) => s.link)
  const unlink = useCoverageStore((s) => s.unlink)
  const acceptSuggestion = useCoverageStore((s) => s.acceptSuggestion)
  const rejectSuggestion = useCoverageStore((s) => s.rejectSuggestion)

  // Bid-level section flags → amber dot on any chip whose section was touched by
  // an accepted addendum change (loaded by the coverage matrix container).
  const bidFlags = useSectionFlagsStore((s) => s.flagsByBid[bidId])
  const flaggedKeys = useMemo(() => mergeFlagsByArtifactSection(bidFlags ?? []), [bidFlags])

  const row = useMemo(() => {
    const rows = requirementKind === 'compliance' ? coverage?.compliance : coverage?.criteria
    return rows?.find((r) => r.requirementId === requirementId)
  }, [coverage, requirementKind, requirementId])

  const rowSuggestions = useMemo(
    () => suggestions.filter((s) => s.requirementId === requirementId),
    [suggestions, requirementId]
  )

  if (!coverage || !row) {
    return <span className="text-meta">—</span>
  }

  const status = coverageStatusConfig[row.status]

  // Parent criteria: pure display rollup — pill only, no chips, no mapping.
  if (row.isRollup) {
    return (
      <Badge
        variant={status.variant}
        className="px-1.5 py-0 text-[11px] font-normal"
        title="Rolled up from sub-criteria"
      >
        {status.label}
      </Badge>
    )
  }

  const linkedKeys = new Set(row.links.map((l) => `${l.artifactId}:${l.sectionKey}`))

  return (
    <div className="group/coverage flex flex-wrap items-center gap-1">
      <Badge variant={status.variant} className="px-1.5 py-0 text-[11px] font-normal">
        {status.label}
      </Badge>

      {row.links.map((sectionLink) => {
        const flag = flaggedKeys.get(`${sectionLink.artifactId}:${sectionLink.sectionKey}`)
        return (
        <span
          key={sectionLink.id}
          className="group/chip inline-flex max-w-[180px] items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px]"
          title={
            flag
              ? `${sectionLink.artifactName} · ${sectionLink.sectionTitle} — affected by ${flag.sourceLabel ?? 'an addendum change'}`
              : `${sectionLink.artifactName} · ${sectionLink.sectionTitle}`
          }
        >
          {flag && (
            <Flag className="h-2.5 w-2.5 shrink-0 text-[hsl(var(--status-warning))]" aria-label={`Affected by ${flag.sourceLabel ?? 'an addendum change'}`} />
          )}
          {sectionLink.source === 'ai' && <GradientSparkleIcon className="h-2.5 w-2.5 shrink-0" />}
          <Link
            to={
              // A whole-document link belongs to a single document, which has
              // its own editor route — `/sections/__document__` is not a section.
              sectionLink.sectionKey === DOCUMENT_SECTION_KEY
                ? `/app/bids/${bidId}/responses/${sectionLink.artifactId}/document`
                : `/app/bids/${bidId}/responses/${sectionLink.artifactId}/sections/${sectionLink.sectionKey}`
            }
            className="truncate hover:text-[hsl(var(--brand))]"
          >
            <span className="text-muted-foreground">{abbreviateArtifact(sectionLink.artifactName)} · </span>
            {sectionLink.sectionTitle}
          </Link>
          {!readOnly && (
            <button
              type="button"
              aria-label={`Unlink ${sectionLink.sectionTitle}`}
              onClick={() => void unlink(bidId, sectionLink.id)}
              className="hidden shrink-0 text-muted-foreground hover:text-destructive group-hover/chip:inline-flex"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
        )
      })}

      {rowSuggestions.map((suggestion) => (
        <span
          key={`${suggestion.artifactId}:${suggestion.sectionKey}`}
          className="inline-flex max-w-[200px] items-center gap-1 rounded-md border border-dashed border-[hsl(var(--brand)/0.5)] bg-[hsl(var(--brand)/0.04)] px-1.5 py-0.5 text-[11px]"
          title={suggestion.reason}
        >
          <GradientSparkleIcon className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">
            <span className="text-muted-foreground">{abbreviateArtifact(suggestion.artifactName)} · </span>
            {suggestion.sectionTitle}
          </span>
          <button
            type="button"
            aria-label="Accept suggestion"
            onClick={() =>
              void acceptSuggestion(bidId, suggestion, {
                artifactName: suggestion.artifactName,
                sectionTitle: suggestion.sectionTitle,
              })
            }
            className="shrink-0 text-[hsl(var(--status-success))] hover:opacity-70"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            type="button"
            aria-label="Reject suggestion"
            onClick={() => rejectSuggestion(suggestion)}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      {!readOnly && (
        <SectionPickerPopover
          bidId={bidId}
          suggestions={rowSuggestions}
          linkedKeys={linkedKeys}
          onSelect={(target) =>
            void link(
              bidId,
              requirementKind === 'compliance'
                ? { complianceItemId: requirementId, artifactId: target.artifactId, sectionKey: target.sectionKey }
                : { evaluationCriterionId: requirementId, artifactId: target.artifactId, sectionKey: target.sectionKey },
              { artifactName: target.artifactName, sectionTitle: target.sectionTitle }
            )
          }
        >
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-0.5 rounded-md px-1 py-0.5 text-[11px] text-muted-foreground',
              'opacity-0 transition-opacity hover:bg-accent hover:text-foreground',
              'focus-visible:opacity-100 group-hover/coverage:opacity-100',
              row.links.length === 0 && 'opacity-100'
            )}
          >
            <Plus className="h-3 w-3" />
            Map
          </button>
        </SectionPickerPopover>
      )}
    </div>
  )
}
